import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { deletePendingPositionApi } from '@flipster/apis/v2/trade';
import type { PendingPosition } from '@flipster/r230522/original/order';
import { SpotPendingPosition } from '@flipster/r230522/original/spot-order';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { useSendApi } from '@flipster/react/misc/use-send-api';
import { getErrorMessage } from '@flipster/utils/error';
import { getFlipsterConfigsV1Key, getLocalStorage } from '@flipster/utils/localStorage';

import { fireYourPositionsManageClickEvent } from '../../../position-close-modal/segment';
import { tradeModeBunja } from '../../../trade-mode/state';
import { useAuth } from '../../auth/state';
import { orderAttributionAtom } from '../../order-attribution';
import { buildTradeRequestBase } from '../base-params';

interface UseCancelPendingOrderOptions {
  /** Sub-account ID for copy trading. If omitted, treated as main account. */
  accountId?: number;
}

/**
 * pending 주문 취소 액션 (구 useClosePosition.cancelPosition).
 * useSendApi(스로틀 + in-flight 억제) 시맨틱과 per-instance 로딩을 그대로 보존하고,
 * 성공/실패와 무관하게 finally에서 segment(Cancel)를 발화하는 기존 동작을 유지한다.
 */
export const useCancelPendingOrder = ({ accountId }: UseCancelPendingOrderOptions = {}) => {
  const transAssets = useTranslations('Assets.r250401');
  const { tradeModeAtom } = useBunja(tradeModeBunja);
  const tradeMode = useAtomValue(tradeModeAtom);
  const attribution = useAtomValue(orderAttributionAtom);
  const { user } = useAuth();

  const [isCancelLoading, cancelPosition] = useSendApi(
    async (o: PendingPosition | SpotPendingPosition, tradeType: 'perpetual' | 'spot') => {
      try {
        await deletePendingPositionApi(o.symbol, o.orderId, {
          ...buildTradeRequestBase(attribution),
          accountId,
        });
        snackbar.success({ message: transAssets('Order_canceled_message') });
      } catch (error) {
        snackbar.error({ message: getErrorMessage(error) });
      } finally {
        if (tradeMode && user) {
          const key = getFlipsterConfigsV1Key(user.userId, tradeType === 'perpetual' ? 'showAllPerp' : 'showAllSpot');
          fireYourPositionsManageClickEvent('Cancel', {
            ...o,
            fundsInvested: o.amount,
            mode: tradeMode,
            isShowAll: getLocalStorage(key) ?? true,
            isProfitBoostMode: false, // pending position은 Profit Boost 모드가 없음
          });
        }
      }
    },
  );

  return { isCancelLoading, cancelPosition };
};
